import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Heart } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then(({ data }) => {
        setPost(data);
        document.title = `${lang === "en" ? data.title_en : data.title_fr} – Fondation Cœur Solidaire`;
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  const title = post ? (lang === "en" ? post.title_en : post.title_fr) : "";
  const content = post ? (lang === "en" ? post.content_en : post.content_fr) : "";

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="blog-post-page">
      <Navbar />

      {loading ? (
        <div className="container-narrow pt-40 pb-20 text-center text-[#64748B]">{t.blogPage.loading}</div>
      ) : !post ? (
        <div className="container-narrow pt-40 pb-20 text-center">
          <p className="text-[#64748B] mb-6">{t.blogPage.notFound}</p>
          <button onClick={() => navigate("/actualites")} className="btn-outline-dark">
            {t.blogPage.allArticles}
          </button>
        </div>
      ) : (
        <>
          <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#002B49] text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #C5A059 0%, transparent 50%)" }} />
            <div className="container-narrow relative max-w-3xl">
              <Link to="/actualites" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors" data-testid="blog-back-list">
                <ArrowLeft size={14} /> {t.blogPage.allArticles}
              </Link>
              <div className="flex items-center gap-3 text-xs text-white/70 mb-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {new Date(post.created_at).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { year: "numeric", month: "long", day: "numeric" })}</span>
                {post.category && <span className="inline-flex items-center gap-1.5 text-[#E8C77E] font-medium uppercase tracking-wider"><Tag size={11} /> {post.category}</span>}
              </div>
              <h1 className="font-serif text-3xl md:text-5xl font-medium leading-[1.15]" data-testid="blog-post-title">
                {title}
              </h1>
            </div>
          </section>

          {post.image_url && (
            <div className="container-narrow -mt-2 max-w-4xl">
              <motion.img
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                src={post.image_url}
                alt={title}
                className="w-full aspect-video object-cover rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,43,73,0.3)]"
              />
            </div>
          )}

          <article className="py-16 md:py-20">
            <div className="container-narrow max-w-3xl">
              <div className="prose prose-lg max-w-none text-[#334155] font-serif" data-testid="blog-post-content">
                {content.split("\n\n").map((para, i) => (
                  <p key={i} className="leading-[1.85] text-[17px] md:text-lg mb-6 first:text-xl first:font-medium first:text-[#0A192F]">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-14 pt-10 border-t border-black/10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <Link to="/actualites" className="text-sm text-[#64748B] hover:text-[#002B49] inline-flex items-center gap-1.5">
                  <ArrowLeft size={14} /> {t.blogPage.allArticles}
                </Link>
                <Link to="/#donate" className="btn-primary inline-flex items-center" data-testid="blog-cta-donate">
                  <Heart size={16} className="mr-2" fill="currentColor" />
                  {t.blogPage.ctaDonate}
                </Link>
              </div>
            </div>
          </article>
        </>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
